import { mergeTargets } from './target-factory.js';
import * as EVENTS from './events.js';

let _tracker = [];
let _root;

const monitoringConfig = {
  subtree: true,
  childList: true,
  attributes: false,
};
const observer = new MutationObserver(callback);

export function startSession(root, tracker) {
  _root = root;
  if (_tracker.length) {
    tracker.push(..._tracker);
  }
  _tracker = tracker;
  observer.observe(root, monitoringConfig);
  root.addEventListener('click', onClick, { capture: true, passive: true });
}

export function closeSession() {
  observer.disconnect();
  _root.removeEventListener('click', onClick, { capture: true, passive: true });
}

function onClick(event) {
  if (event.target.form && event.target.type === 'submit') {
    const form = event.target.form;
    console.debug('special case: form submit', form);
    /*
For clicks on for forms submitter we must iterate over all form element
 and track them as fake ui click
     */
    for (const element of form.elements) {
      if (element.dataset.etEvent === 'submit:ui_click') {
        const ui = {
          element_type: element.dataset.etElType,
          element_code: element.dataset.etElCode,
          element_text: element.value.toString() ?? 'not_found',
        };
        const payload = {
          ui,
          feed: getFeedOf(element),
          target: getTargetOf(element),
          interactive: getInteractiveOf(element),
        };
        if (payload.interactive) {
          // `options` present in every etInteractive, but required only for impressions
          payload.interactive.options = undefined;
          // as well as `option_points` in case of quiz
          payload.interactive.option_points = undefined;
        }
        _tracker.push([EVENTS.UI_CLICK, payload]);
      }
    }
  }

  const trackingTarget = event.target.closest('[data-et-event^="click"]');

  if (!trackingTarget) {
    return;
  }
  const [eventName, eventAlias] = trackingTarget.dataset.etEvent.split(':');

  const ui = {
    element_type: trackingTarget.dataset.etElType ?? 'button',
    element_code:
      trackingTarget.dataset.etElCode ?? trackingTarget.getAttribute('id'),
    element_text:
      trackingTarget.dataset.etElText ?? trackingTarget.innerText ?? '',
  };

  if (trackingTarget.dataset.etElAutoClick === 'true') {
    ui.element_code = `auto_${ui.element_code}`;
  }

  const payload = {
    target: getTargetOf(trackingTarget),
    feed: getFeedOf(trackingTarget),
    ui,
    interactive: getInteractiveOf(trackingTarget),
  };

  if (payload.interactive) {
    // `options` present in every etInteractive, but required only for impressions
    payload.interactive.options = undefined;
  }
  _tracker.push([eventAlias ?? eventName, payload]);
}

function callback(mutationsList, _observer) {
  for (let mutation of mutationsList) {
    if (mutation.type === 'childList') {
      for (let node of mutation.addedNodes) {
        const impressionNode =
          node.dataset?.etEvent === EVENTS.IMPRESSION
            ? node
            : node.querySelector?.(`[data-et-event="${EVENTS.IMPRESSION}"]`);

        if (impressionNode) {
          const payload = getTrackPayloadForNode(impressionNode);
          _tracker.push([EVENTS.IMPRESSION, payload]);
        }
      }
    }
  }
}

function getTrackPayloadForNode(node) {
  const targetSource = node.closest('[data-et-target]');
  if (!targetSource) {
    console.error('Missing target for tracking');
    return;
  }

  return {
    target: getTargetOf(node),
    feed: getFeedOf(node),
    interactive: getInteractiveOf(node),
  };
}

function getInteractiveOf(node) {
  const interactiveSource = node.closest('[data-et-interactive]');
  if (interactiveSource) {
    return JSON.parse(interactiveSource.dataset.etInteractive);
  }
}

function getFeedOf(node) {
  const feedSource = node.closest('[data-et-feed]');
  if (!feedSource) {
    console.error('Missing feed for tracking');
    return;
  }
  return JSON.parse(feedSource.dataset.etFeed);
}

function getTargetOf(node) {
  const sources = [];
  let targetSource = node.closest('[data-et-target]');
  while (targetSource) {
    sources.push(targetSource);
    targetSource = targetSource.parentElement.closest('[data-et-target]');
  }

  const target = sources
    .filter(Boolean)
    .filter(el => Boolean(el.dataset.etTarget))
    .reduce((target, el) => {
      return Object.assign(target, JSON.parse(el.dataset.etTarget));
  }, {});

  const campaignSource = node.closest('[data-et-campaign]');
  if (campaignSource) {
    const campaign = JSON.parse(campaignSource.dataset.etCampaign);
    return mergeTargets(target, campaign);
  }

  return target;
}
